// ═══════════════════════════════════════════════════════════════
// VaultZkActivationWizard.js — Activation V2 zero-knowledge (Session G)
// ═══════════════════════════════════════════════════════════════
//
// Wizard 3 étapes pour activer le mode renforcé :
//   1. Choix passphrase (avec meter + confirmation)
//   2. Affichage des 24 mots BIP39 + warning + bouton noté
//   3. Vérification (re-saisie de 4 mots aux positions imposées)
// ═══════════════════════════════════════════════════════════════

import React, { useMemo, useState } from 'react';
import {
    Shield, Eye, EyeOff, Copy, Check, AlertTriangle, Loader2, ChevronRight, Lock,
} from 'lucide-react';
import {
    buildActivationPayload, passphraseStrengthScore, VAULT_ZK_KDF_LABEL,
} from '../../utils/vaultZkCrypto';
import { activateZk } from '../../services/vaultZkApi';
import { useVaultZk } from './VaultZkContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';

const STRENGTH_LABELS = ['fragile', 'faible', 'correcte', 'forte', 'excellente'];
const STRENGTH_COLORS = ['#dc2626', '#f97316', '#eab308', '#22c55e', '#10b981'];

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 8, 18, 0.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
    },
    modal: (isDark) => ({
        width: 'min(560px, 100%)', maxHeight: '90vh', overflow: 'auto',
        background: isDark ? '#13131a' : '#ffffff',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        borderRadius: 16, padding: '28px 32px 24px',
        boxShadow: '0 24px 48px rgba(76, 29, 149, 0.35)',
        border: isDark ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.18)',
    }),
    title: { fontSize: 19, fontWeight: 600, margin: '6px 0 4px' },
    sub: (isDark) => ({ fontSize: 13.5, color: isDark ? '#9090a8' : '#6a6a85', margin: '0 0 20px', lineHeight: 1.55 }),
    label: { fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'block' },
    input: (isDark) => ({
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px', fontSize: 14,
        background: isDark ? '#1c1c28' : '#f7f7fb',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 8, outline: 'none', fontFamily: 'inherit',
    }),
    inputGroup: { position: 'relative', marginBottom: 12 },
    eyeBtn: (isDark) => ({
        position: 'absolute', right: 10, top: 9, background: 'transparent',
        border: 'none', cursor: 'pointer', color: isDark ? '#9090a8' : '#6a6a85', padding: 4,
    }),
    err: { color: '#ef4444', fontSize: 12.5, marginTop: 6, marginBottom: 10 },
    btnPrimary: (busy) => ({
        width: '100%', padding: '11px 14px',
        background: busy ? '#6d28d9' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        color: '#fff', border: 'none', borderRadius: 9,
        fontSize: 14, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 6px 18px rgba(109, 40, 217, 0.32)',
    }),
    btnGhost: (isDark) => ({
        padding: '9px 14px',
        background: 'transparent',
        color: isDark ? '#b8b8c8' : '#5a5a72',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 9, fontSize: 13.5, cursor: 'pointer',
    }),
    meter: { height: 5, background: '#2c2c3c', borderRadius: 99, marginTop: 6, overflow: 'hidden' },
    meterBar: (score) => ({
        height: '100%', width: `${(score / 4) * 100}%`,
        background: STRENGTH_COLORS[score] || '#6a6a85',
        transition: 'width 200ms ease, background 200ms ease',
    }),
    steps: { display: 'flex', gap: 6, marginBottom: 20 },
    stepDot: (active, done) => ({
        flex: 1, height: 4, borderRadius: 2,
        background: done ? '#8b5cf6' : active ? '#a78bfa' : '#2c2c3c',
        transition: 'background 200ms',
    }),
    mnemonicGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8, padding: 16, marginBottom: 14,
        background: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.25)',
        borderRadius: 10, fontFamily: 'monospace', fontSize: 13,
    },
    mnemonicWord: (isDark) => ({
        display: 'flex', alignItems: 'baseline', gap: 6,
        padding: '4px 8px', borderRadius: 4,
        background: isDark ? 'rgba(28, 28, 40, 0.6)' : 'rgba(255, 255, 255, 0.7)',
    }),
    wordIdx: { fontSize: 10, color: '#8b5cf6', fontWeight: 600, minWidth: 18 },
    warn: {
        background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: 9, padding: 12, fontSize: 12.5, lineHeight: 1.55,
        marginBottom: 14, color: '#f59e0b',
        display: 'flex', gap: 9, alignItems: 'flex-start',
    },
    checkRow: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, fontSize: 12.5, lineHeight: 1.45 },
    confirmGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 },
    confirmCell: (isDark) => ({
        padding: 10, borderRadius: 8,
        background: isDark ? 'rgba(28, 28, 40, 0.6)' : 'rgba(247, 247, 251, 0.85)',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
    }),
};

export default function VaultZkActivationWizard({ onClose, onActivated }) {
    const { unlock, refreshChallenge } = useVaultZk();
    const { isDark } = useTheme();
    const [step, setStep] = useState(1);
    const [pwd1, setPwd1] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [mnemonic, setMnemonic] = useState(null);
    const [activationPayload, setActivationPayload] = useState(null);
    const [activationDek, setActivationDek] = useState(null);
    const [confirmIdx, setConfirmIdx] = useState([]); // 4 indices à re-saisir
    const [confirmAnswers, setConfirmAnswers] = useState(['', '', '', '']);
    const [acknowledged, setAcknowledged] = useState(false);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const score = useMemo(() => passphraseStrengthScore(pwd1), [pwd1]);

    function nextFromStep1() {
        setErr('');
        if (pwd1.length < 12) { setErr('Au moins 12 caractères pour la passphrase.'); return; }
        if (score < 2) { setErr('Renforcez la passphrase (mélangez majuscules, chiffres, symboles).'); return; }
        if (pwd1 !== pwd2) { setErr('Les deux passphrases ne correspondent pas.'); return; }
        setBusy(true);
        // Génère DEK + mnémonique + tous les wrappers
        buildActivationPayload(pwd1)
            .then(({ payload, mnemonic, dek }) => {
                setMnemonic(mnemonic);
                setActivationPayload(payload);
                setActivationDek(dek);
                // 4 indices aléatoires DISTINCTS dans [0..23] pour vérification étape 3
                const all = Array.from({ length: 24 }, (_, i) => i);
                for (let i = all.length - 1; i > 0; i -= 1) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [all[i], all[j]] = [all[j], all[i]];
                }
                setConfirmIdx(all.slice(0, 4).sort((a, b) => a - b));
                setStep(2);
            })
            .catch((e) => setErr(`Erreur génération clés : ${e.message}`))
            .finally(() => setBusy(false));
    }

    function nextFromStep2() {
        setErr('');
        if (!acknowledged) { setErr('Cochez la case pour confirmer que vous avez noté la passphrase ET le mnémonique.'); return; }
        setStep(3);
        setAcknowledged(false);
        setConfirmAnswers(['', '', '', '']);
    }

    async function activate() {
        setErr('');
        // Vérifier les 4 mots saisis
        for (let i = 0; i < 4; i += 1) {
            const expected = mnemonic[confirmIdx[i]];
            const got = (confirmAnswers[i] || '').toLowerCase().trim();
            if (got !== expected) {
                setErr(`Mot ${confirmIdx[i] + 1} incorrect — vérifiez votre liste de 24 mots.`);
                return;
            }
        }
        if (!acknowledged) {
            setErr('Cochez la case finale pour confirmer.');
            return;
        }
        setBusy(true);
        try {
            await activateZk(activationPayload);
            unlock(activationDek);
            await refreshChallenge();
            // Wipe les copies sensibles côté frontend
            setPwd1(''); setPwd2(''); setMnemonic(null); setActivationPayload(null); setActivationDek(null);
            toast.success('Mode renforcé activé — coffre-fort déverrouillé');
            if (onActivated) onActivated();
            if (onClose) onClose();
        } catch (e) {
            setErr(`Activation refusée : ${e.message || e.code || 'erreur inconnue'}`);
        } finally {
            setBusy(false);
        }
    }

    async function copyMnemonic() {
        try {
            await navigator.clipboard.writeText(mnemonic.join(' '));
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // navigator.clipboard absent ou refusé → l'user fera copier manuellement
        }
    }

    return (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Activation mode renforcé">
            <div style={styles.modal(isDark)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Shield size={20} color="#8b5cf6" />
                    <h2 style={styles.title}>
                        {step === 1 && 'Activer le mode renforcé'}
                        {step === 2 && 'Vos 24 mots de récupération'}
                        {step === 3 && 'Vérification finale'}
                    </h2>
                </div>
                <p style={styles.sub(isDark)}>
                    {step === 1 && 'Choisissez une passphrase qui ne quittera jamais cet onglet. Elle dérive la clé qui chiffre vos items.'}
                    {step === 2 && 'Notez ces 24 mots dans l\'ordre — c\'est la SEULE façon de récupérer vos items si vous oubliez la passphrase.'}
                    {step === 3 && 'Re-saisissez les 4 mots demandés pour confirmer que vous les avez bien notés.'}
                </p>

                <div style={styles.steps}>
                    <div style={styles.stepDot(step >= 1, step > 1)} />
                    <div style={styles.stepDot(step >= 2, step > 2)} />
                    <div style={styles.stepDot(step >= 3, false)} />
                </div>

                {step === 1 && (
                    <>
                        <label style={styles.label}>Passphrase</label>
                        <div style={styles.inputGroup}>
                            <input
                                type={showPwd ? 'text' : 'password'}
                                value={pwd1}
                                onChange={(e) => setPwd1(e.target.value)}
                                style={{ ...styles.input(isDark), paddingRight: 38 }}
                                autoFocus
                                placeholder="Au moins 12 caractères, idéalement 16+"
                            />
                            <button type="button" style={styles.eyeBtn(isDark)} onClick={() => setShowPwd(!showPwd)}>
                                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <div style={styles.meter}><div style={styles.meterBar(score)} /></div>
                        <div style={{ fontSize: 11, color: STRENGTH_COLORS[score], marginTop: 4, marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            {STRENGTH_LABELS[score]}
                        </div>

                        <label style={styles.label}>Confirmer la passphrase</label>
                        <div style={styles.inputGroup}>
                            <input
                                type={showPwd ? 'text' : 'password'}
                                value={pwd2}
                                onChange={(e) => setPwd2(e.target.value)}
                                style={styles.input(isDark)}
                                onKeyDown={(e) => { if (e.key === 'Enter') nextFromStep1(); }}
                            />
                        </div>

                        {err && <div style={styles.err}>{err}</div>}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button style={styles.btnGhost(isDark)} onClick={onClose}>Annuler</button>
                            <button style={styles.btnPrimary(busy)} onClick={nextFromStep1} disabled={busy}>
                                {busy ? <Loader2 size={16} className="spin" /> : <ChevronRight size={16} />}
                                Continuer
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && mnemonic && (
                    <>
                        <div style={styles.warn}>
                            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>
                                <strong>Notez ces mots maintenant.</strong> Si vous oubliez votre passphrase ET perdez ce mnémonique,
                                vos items chiffrés seront définitivement irrécupérables — y compris pour l'administrateur du serveur.
                            </div>
                        </div>

                        <div style={styles.mnemonicGrid}>
                            {mnemonic.map((w, i) => (
                                <div key={i} style={styles.mnemonicWord(isDark)}>
                                    <span style={styles.wordIdx}>{i + 1}.</span>
                                    <span>{w}</span>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                            <button style={styles.btnGhost(isDark)} onClick={copyMnemonic}>
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                <span style={{ marginLeft: 6 }}>{copied ? 'Copié' : 'Copier les 24 mots'}</span>
                            </button>
                        </div>

                        <label style={styles.checkRow}>
                            <input
                                type="checkbox" checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                style={{ marginTop: 3 }}
                            />
                            <span>J'ai noté la passphrase ET le mnémonique 24 mots dans un endroit sûr (gestionnaire de mots de passe, papier, coffre).</span>
                        </label>

                        {err && <div style={styles.err}>{err}</div>}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button style={styles.btnGhost(isDark)} onClick={() => setStep(1)}>← Retour</button>
                            <button style={styles.btnPrimary(busy)} onClick={nextFromStep2} disabled={busy || !acknowledged}>
                                <ChevronRight size={16} />
                                Continuer
                            </button>
                        </div>
                    </>
                )}

                {step === 3 && mnemonic && (
                    <>
                        <p style={{ ...styles.sub(isDark), marginBottom: 14 }}>
                            Saisissez les 4 mots aux positions demandées. Ne les copiez pas depuis l'écran précédent — utilisez votre note.
                        </p>

                        <div style={styles.confirmGrid}>
                            {confirmIdx.map((idx, i) => (
                                <div key={idx} style={styles.confirmCell(isDark)}>
                                    <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600, marginBottom: 4, letterSpacing: 0.4 }}>
                                        MOT N° {idx + 1}
                                    </div>
                                    <input
                                        type="text" value={confirmAnswers[i]}
                                        onChange={(e) => {
                                            const next = [...confirmAnswers];
                                            next[i] = e.target.value;
                                            setConfirmAnswers(next);
                                        }}
                                        style={{ ...styles.input(isDark), fontFamily: 'monospace', padding: '7px 10px' }}
                                        autoComplete="off" spellCheck="false"
                                    />
                                </div>
                            ))}
                        </div>

                        <label style={styles.checkRow}>
                            <input
                                type="checkbox" checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                style={{ marginTop: 3 }}
                            />
                            <span>Je comprends qu'aucune récupération n'est possible sans la passphrase ou le mnémonique — y compris par l'administrateur.</span>
                        </label>

                        {err && <div style={styles.err}>{err}</div>}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button style={styles.btnGhost(isDark)} onClick={() => setStep(2)}>← Retour</button>
                            <button style={styles.btnPrimary(busy)} onClick={activate} disabled={busy || !acknowledged}>
                                {busy ? <Loader2 size={16} className="spin" /> : <Lock size={16} />}
                                Activer le mode renforcé
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
